<template>
  <q-dialog
    @before-hide="$emit('close')"
    @before-show="initializeDialog"
    position="bottom"
    transition-show="slide-up"
    transition-hide="slide-down"
    class="event"
  >
    <q-card class="event-card">
      <q-card-section class="flex justify-between">
        <div class="text-h6">
          {{ $t(isEditing ? "events.editEvent" : "events.addEvent") }}
        </div>
        <q-btn dense flat icon="close" v-close-popup>
          <q-tooltip class="bg-white text-primary">{{
            $t("common.close")
          }}</q-tooltip>
        </q-btn>
      </q-card-section>

      <q-card-section>
        <div
          v-if="loadingEventData"
          class="flex justify-center items-center q-pa-lg"
        >
          <q-spinner color="primary" size="3em" />
        </div>

        <EventSponsorImageForm
          v-if="canEditEventSponsorImage"
          :is-loading="sponsorImageFormLoader"
          :form-data="formData"
          :event="event"
          @form-submit="handleSubmitSponsorImage"
          @update="updateFormData"
        />

        <EventForm
          v-if="canEditEvent(event)"
          v-show="!loadingEventData"
          :form-data="formData"
          :is-editing="isEditing"
          :event-id="eventId"
          :is-loading="isLoading"
          :countries-options="countriesOptions"
          @form-submit="handleSubmit"
          @update="updateFormData"
        />
      </q-card-section>
    </q-card>
  </q-dialog>
</template>

<script>
import { date } from "quasar";
import { mapActions, mapState } from "vuex";
import { Notify } from "quasar";
import EventForm from "./EventForm.vue";
import EventSponsorImageForm from "./EventSponsorImageForm.vue";
import userRoleMixin from "@/mixins/userRoleMixin.js";

export default {
  name: "EventDialog",
  components: {
    EventForm,
    EventSponsorImageForm
  },
  props: {
    event: {
      type: Object,
      required: true,
    },
    eventId: {
      type: Number,
      default: null,
    },
    isVerified: {
      type: Boolean,
      default: false,
    }
  },
  mixins: [userRoleMixin],
  data() {
    return {
      formData: this.getInitialFormState(),
      isLoading: false,
      sponsorImageFormLoader: false,
      loadingEventData: false,
    };
  },
  computed: {
    ...mapState({
      countriesList: (state) => state.global.countriesList,
      unverifiedEvents: (state) => state.dashboardEvents.unverifiedEvents,
    }),
    countriesOptions() {
      return this.countriesList;
    },
    isEditing() {
      return !!this.eventId;
    },
  },
  methods: {
    ...mapActions({
      createEventVerification: "dashboardEvents/createEventVerification",
      updateEvent: "dashboardEvents/updateEvent",
      updateEventSponsorImage: "dashboardEvents/updateEventSponsorImage",
      getEventFormData: "dashboardEvents/getEventFormData",
      getCountries: "global/getCountries",
    }),
    getInitialFormState() {
      const baseState = {
        eventTitle: "",
        eventCountry: null,
        city: "",
        eventDate: null,
        image: null,
        photoPrice: null,
        eventSponsorImage: null
      };

      const roleFields = {
        sponsor: {},
        photographer: {
          albumPrice: null,
        },
        organizer: {
          albumPrice: null,
          authorsCommission: null,
          hasBib: true,
        }
      };

      return {
        ...baseState,
        ...(roleFields[this.userRole] || roleFields.organizer)
      };
    },
    updateFormData(newData) {
      this.formData = { ...this.formData, ...newData };
    },
    async initializeDialog() {
      this.formData = this.getInitialFormState();

      if (this.eventId && this.event && this.canEditEvent(this.event)) {
        await this.fetchEventData();
      }
    },
    async fetchEventData() {
      this.loadingEventData = true;

      try {
        const action = this.isUnverifiedEvent(this.eventId)
          ? "verification"
          : "event";

        const eventData = await this.getEventFormData({
          action,
          eventId: this.eventId,
        });

        this.formData = this.transformEventData(eventData);
      } catch (error) {
        this.showNotification("events.form.fetchError", "negative");
      } finally {
        this.loadingEventData = false;
      }
    },
    transformEventData(eventData) {
      const baseData = {
        eventTitle: eventData.eventTitle,
        eventDate: eventData.eventDate,
        city: eventData.eventCity,
        eventCountry: this.mapCountryById(eventData.eventCountry),
        image: null,
        photoPrice: eventData.organizerPhotoPrice,
      };

      const roleTransforms = {
        sponsor: {},
        photographer: {
          albumPrice: eventData.organizerAlbumPrice,
        },
        organizer: {
          albumPrice: eventData.organizerAlbumPrice,
          authorsCommission: eventData.organizerAuthorCommission,
          hasBib: eventData.hasBib,
        },
      };

      return {
        ...baseData,
        ...(roleTransforms[this.userRole] || roleTransforms.organizer)
      };
    },
    isUnverifiedEvent(eventId) {
      return this.eventId && this.unverifiedEvents.some((event) => event.id === eventId) || !this.eventId;
    },
    mapCountryById(countryId) {
      return (
        this.countriesList.find((country) => country.id === countryId) || null
      );
    },
    formatEventDate(dateValue) {
      if (/^\d{8}$/.test(dateValue)) {
        return date.extractDate(dateValue, "YYYYMMDD");
      }

      return dateValue;
    },
    buildFormFields() {
      const baseFields = {
        event_title: this.formData.eventTitle,
        event_date: date.formatDate(this.formatEventDate(this.formData.eventDate), "YYYY/MM/DD"),
        photo_price: this.formData.photoPrice,
      };

      const roleFieldsMapping = {
        sponsor: {},
        photographer: {
          album_price: this.formData.albumPrice,
        },
        organizer: {
          album_price: this.formData.albumPrice,
          authors_commission: this.formData.authorsCommission,
          has_bib: this.formData.hasBib,
        },
      };

      return { ...baseFields, ...(roleFieldsMapping[this.userRole] || roleFieldsMapping.organizer) };
    },
    createFormData() {
      const formData = new FormData();
      const allFields = this.buildFormFields();

      Object.entries(allFields).forEach(([key, value]) => {
        formData.append(key, value);
      });

      if (this.formData.image instanceof File) {
        formData.append("image", this.formData.image);
      }

      if (this.isUnverifiedEvent(this.eventId)) {
        formData.append("city", this.formData.city);
        formData.append("event_country", this.formData.eventCountry.id);
      }

      formData.append("user_role", this.userRole);

      return formData;
    },
    async handleSubmitSponsorImage() {
      if (this.sponsorImageFormLoader) return;

      this.sponsorImageFormLoader = true;

      try {
        const payload = {
          sponsor_image: this.formData.eventSponsorImage
        };

        await this.updateEventSponsorImage({
          payload: payload,
          eventId: this.eventId
        });
      } catch (error) {
        Notify.create({
          type: 'negative',
          message: this.$t('events.sponsorPhoto.formError'),
          position: 'bottom',
        });

        return;
      } finally {
        this.sponsorImageFormLoader = false;
      }

      Notify.create({
        type: 'positive',
        message: this.$t('events.sponsorPhoto.formSuccess'),
        position: 'bottom',
      });

      this.$emit('close');
    },
    async handleSubmit() {
      if (this.isLoading) return;
      if (!this.canAddEvent) return;
      this.isLoading = true;

      try {
        const formData = this.createFormData();
        if (this.isEditing) {
          const action = this.isUnverifiedEvent(this.eventId)
            ? "verification"
            : "event";
          await this.updateEvent({
            action,
            eventId: this.eventId,
            eventData: formData,
          });
          this.$emit("update-event");
        } else {
          await this.createEventVerification(formData);
          this.$emit("create-event");
        }

        this.$emit("close");
        this.formData = this.getInitialFormState();
        this.showNotification(
          this.isEditing ? "events.form.updateSuccess" : "events.form.success",
          "positive"
        );
      } catch (error) {
        this.showNotification(
          error?.message || "events.form.error",
          "negative"
        );
      } finally {
        this.isLoading = false;
      }
    },
    showNotification(message, type) {
      Notify.create({
        message: this.$t(message),
        type,
        position: "bottom",
        timeout: 3000,
      });
    },
  }
};
</script>

<style lang="scss" scoped>
.event {
  &-card {
    min-height: 400px;
    padding-right: 10px;
    overflow: auto;

    @media (min-width: $breakpoint-xs-max) {
      min-width: 600px;
    }
  }
}
</style>